<template>
  <v-form ref="form" v-model="valid">
    <v-text-field
      v-model="localValue.name"
      type="text"
      data-testid="add-workspace-modal-domain"
      class="mb-6"
      :rules="[required()]"
      :placeholder="$t(`modals.${ScheduledReportsAction.ADD_EXPORT}.name.label`)"
      :label="$t(`modals.${ScheduledReportsAction.ADD_EXPORT}.name.label`)"
      :aria-label="$t(`modals.${ScheduledReportsAction.ADD_EXPORT}.name.label`)"
      variant="outlined"
    >
    </v-text-field>
    <div class="subtitle1 mb-4">
      {{ $t(`modals.${ScheduledReportsAction.ADD_EXPORT}.exportType`) }}
    </div>
    <v-select
      v-model="localValue.subType"
      class="mb-6"
      :placeholder="$t(`modals.${ScheduledReportsAction.ADD_EXPORT}.exportType`)"
      :label="$t(`modals.${ScheduledReportsAction.ADD_EXPORT}.exportType`)"
      :items="exportTypes"
      :rules="[required()]"
      variant="outlined"
      :return-object="false"
      :disabled="isEditMode"
    >
      <template #selection="{ item }">
        {{
          item.value
            ? $t(`reports.scheduled.subTypes.${item.value}`)
            : $t(`modals.${ScheduledReportsAction.ADD_EXPORT}.exportType`)
        }}
      </template>
      <template v-slot:item="{ props, item }">
        <v-list-item v-bind="props" :title="$t(`reports.scheduled.subTypes.${item.value}`)">
        </v-list-item>
      </template>
    </v-select>

    <scheduled-export-modal-filters
      class="mb-8"
      v-if="!isSecurityAwarenessType(localValue.subType)"
      v-model:filters="localValue.filters"
      :export-type="localValue.subType"
      @update:filters="localValue.filters = $event"
    />

    <div class="subtitle1 mb-4">
      {{ $t(`modals.${ScheduledReportsAction.ADD_EXPORT}.frequency`) }}
    </div>
    <button-toggle-group
      v-model="localValue.frequency"
      :items="frequencyItems"
      class="mb-6 flex-grow-1 w-100"
      density="comfortable"
      bg-color="indigo-faint"
    />
    <template v-if="isChannelWorkspace && !isSecurityAwarenessType(localValue.subType)">
      <div class="subtitle1 mb-4">
        {{ $t(`modals.${ScheduledReportsAction.ADD_EXPORT}.scope`) }}
      </div>
      <v-radio-group
        data-testid="scheduled-export-scope-radio"
        v-model="localValue.scope"
        hide-details
      >
        <v-radio v-for="scope in scopes" class="mb-4" :key="scope" :value="scope" :ripple="false">
          <template #label>
            <div class="body1">
              {{ $t(`reports.scheduled.scopes.${scope}`) }}
            </div>
          </template>
        </v-radio>
      </v-radio-group>
    </template>

    <div class="subtitle1 mb-4">
      {{ $t(`modals.${ScheduledReportsAction.ADD_EXPORT}.recipients`) }}
    </div>
    <v-radio-group v-model="recipientsChoice" hide-details>
      <v-radio
        v-for="choice in RecipientsChoices"
        class="mb-4"
        :key="choice"
        :value="choice"
        :ripple="false"
      >
        <template #label>
          <div class="body1">
            {{ $t(`modals.${ScheduledReportsAction.ADD_EXPORT}.recipientsChoice.${choice}`) }}
          </div>
        </template>
      </v-radio>
    </v-radio-group>

    <v-autocomplete
      v-if="recipientsChoice === RecipientsChoices.SPECIFIC_ADMIN_USERS"
      v-model="localValue.recipients"
      class="ml-10"
      :placeholder="$t(`modals.${ScheduledReportsAction.ADD_EXPORT}.recipients`)"
      :label="$t(`modals.${ScheduledReportsAction.ADD_EXPORT}.recipients`)"
      item-title="email"
      item-value="email"
      :hide-no-data="false"
      :no-filter="true"
      auto-select-first="exact"
      :rules="[atLeastOneIsRequired()]"
      multiple
      :menu-props="{
        maxHeight: '250',
      }"
      :items="adminUsers"
      variant="outlined"
      chips
      closable-chips
      clear-on-select
      @update:search="onAdminUserSearch($event)"
    >
      <template v-slot:chip="{ props, item }">
        <v-chip
          v-bind="props"
          :closable="true"
          variant="flat"
          size="default"
          color="indigo-faint"
          close-icon="$closeCircle"
          :text="item.title"
        >
          <div class="d-flex align-center">
            <span class="ml-1 mr-1">{{ item.title }}</span>
          </div>
        </v-chip>
      </template>
      <template v-slot:item="{ props, item }">
        <v-list-item v-bind="props" :title="item.title"></v-list-item>
      </template>
    </v-autocomplete>
  </v-form>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType, watch } from "vue";
import { useI18n } from "vue-i18n";
import type { DialogDataConfig } from "@/_store/dialogs.module";
import { atLeastOneIsRequired, required } from "@/_helpers/validators";
import {
  type ScheduledExportItem,
  ScheduledReportFrequency,
  ScheduledType,
} from "@/_store/scheduled-reports/scheduled-reports.module.ts";
import { ScheduledReportsAction } from "@/constants/scheduled-reports";
import ScheduledExportModalFilters from "@/components/modals/psa-modals/ScheduledExportModalFilters.vue";
import { useScheduledReportModal } from "@/composables/useScheduledReportModal";
import { ExportCategory, ExportChoice } from "@/constants/exports.ts";
import ButtonToggleGroup, { type ButtonToggleItem } from "@/components/ButtonToggleGroup.vue";

const defaultScheduledExportItem = {
  id: undefined,
  name: undefined,
  subType: undefined,
  filters: {},
  frequency: ScheduledReportFrequency.DAILY,
  scope: undefined,
  recipients: [],
  type: ScheduledType.EXPORT,
} as unknown as ScheduledExportItem;

export default defineComponent({
  components: { ScheduledExportModalFilters, ButtonToggleGroup },
  props: {
    config: {
      type: Object as PropType<DialogDataConfig<ScheduledExportItem>>,
      required: true,
    },
  },
  setup(props, { emit }) {
    const { t } = useI18n();
    const isEditMode = props.config.action === ScheduledReportsAction.EDIT_EXPORT;
    const {
      valid,
      validate,
      localValue,
      isChannelWorkspace,
      adminUsersLoading,
      adminUsers,
      onAdminUserSearch,
      recipientsChoice,
      RecipientsChoices,
      form,
      frequencies,
      scopes,
    } = useScheduledReportModal(props.config.item, defaultScheduledExportItem, isEditMode);

    const exportTypes = [
      ExportCategory.ACTIVITY_LOGS,
      ExportCategory.DEVICES,
      ExportCategory.USERS,
      ExportCategory.TICKETS,
      ExportCategory.PHISHING_SIMULATION,
      ExportCategory.SECURITY_TRAINING,
    ];

    const frequencyItems = computed<ButtonToggleItem[]>(() =>
      frequencies.value.map((frequency) => ({
        value: frequency,
        label: t(`reports.scheduled.frequencies.${frequency}`),
      }))
    );

    watch(
      localValue,
      (newVal) => {
        emit("update:localValue", newVal);
      },
      { deep: true, immediate: true }
    );

    watch(valid, (value: boolean | null) => {
      emit("update:valid", value ?? true);
    });

    const isSecurityAwarenessType = (type: ExportCategory) => {
      return [ExportCategory.PHISHING_SIMULATION, ExportCategory.SECURITY_TRAINING].includes(type);
    };

    watch(
      () => localValue.value.subType,
      (val) => {
        if (!frequencies.value.includes(localValue.value.frequency)) {
          if (isSecurityAwarenessType(val)) {
            localValue.value.frequency = ScheduledReportFrequency.QUARTERLY;
          } else {
            localValue.value.frequency = ScheduledReportFrequency.DAILY;
          }
        }
      }
    );

    return {
      frequencies,
      scopes,
      valid,
      form,
      recipientsChoice,
      RecipientsChoices,
      isChannelWorkspace,
      isSecurityAwarenessType,
      localValue,
      ExportChoice,
      exportTypes,
      ScheduledReportFrequency,
      ScheduledReportsAction,
      adminUsers,
      onAdminUserSearch,
      required,
      validate,
      atLeastOneIsRequired,
      adminUsersLoading,
      isEditMode,
      frequencyItems,
    };
  },
});
</script>

<style lang="scss" scoped>
.filter {
  width: 240px !important;
}
</style>
